import * as config from "util/config";

/**
 * Whether the transfer summary carries scheduler payload worth rendering (matches v3 ticket mapping).
 *
 * @param {object|null|undefined} scheduler Scheduler object from Redux summary.
 * @returns {boolean}
 */
export function hasSchedulerContent(scheduler) {
    if (!scheduler || typeof scheduler !== "object") {
        return false;
    }
    return Object.values(scheduler).some((value) => value != null && value !== "");
}

/**
 * Resolves the debit account object from summary, enriching `productType` from `accounts` when missing.
 *
 * @param {object|null|undefined} summary Transfer summary from Redux.
 * @param {Array<object>|null|undefined} [accounts] Product accounts list from Redux.
 * @returns {object|null}
 */
function resolveDebitAccount(summary, accounts) {
    const { debitAccount } = summary ?? {};
    if (!debitAccount) {
        return null;
    }

    const catalog = accounts?.length ? accounts : summary?.accounts;

    let resolved = null;
    if (typeof debitAccount === "object" && debitAccount.idProduct) {
        resolved = debitAccount;
    } else if (typeof debitAccount === "string" && catalog?.length) {
        resolved = catalog.find((account) => account.idProduct === debitAccount) ?? null;
    }

    if (!resolved) {
        return null;
    }

    if (resolved.productType || !catalog?.length) {
        return resolved;
    }

    const fromCatalog = catalog.find((account) => account.idProduct === resolved.idProduct);
    if (!fromCatalog?.productType) {
        return resolved;
    }

    return { ...resolved, productType: fromCatalog.productType };
}

/**
 * Normalizes transfer kind to the string stored in legacy summary (`"same"` | `"other"`).
 *
 * @param {string|{ value?: string }|null|undefined} transferKind
 * @returns {string|null|undefined}
 */
function resolveTransferKind(transferKind) {
    if (transferKind && typeof transferKind === "object" && transferKind.value != null) {
        return transferKind.value;
    }
    return transferKind;
}

/**
 * Tax warning for third-party transfers from non-savings accounts on the review screen.
 * Does not depend on scheduling (same rule as `TransferTransactionData`).
 *
 * @param {object|null|undefined} summary Transfer summary from Redux.
 * @param {boolean} internalTransfer Whether the flow is an internal transfer.
 * @param {Array<object>|null|undefined} [accounts] Product accounts list from Redux.
 * @returns {string|null} Configured message, or `null` when the warning does not apply.
 */
export function getTransferTaxNote(summary, internalTransfer, accounts) {
    const { tadTrx, transferKind } = summary ?? {};
    const debitAccount = resolveDebitAccount(summary, accounts);
    const resolvedTransferKind = resolveTransferKind(transferKind);

    const applies =
        !tadTrx &&
        !internalTransfer &&
        Boolean(debitAccount?.idProduct) &&
        debitAccount.productType !== "CA" &&
        resolvedTransferKind === "other";

    if (!applies) {
        return null;
    }

    return config.get(`transferencias.impuestos${debitAccount.productType}.Texto`);
}

/** @deprecated Use {@link getTransferTaxNote}; scheduling is not required. */
export function getScheduledTaxNote(summary, internalTransfer, accounts) {
    return getTransferTaxNote(summary, internalTransfer, accounts);
}
